import { Modal } from "flowbite-react";
import { FormikProvider, getIn, useFormik } from "formik";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { useContext, useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import {
  changeStatusMaintenance,
  maintenanceStartVisualRecords,
} from "../../../../common/services/maintenance.api";
import {
  IMaintenance,
  MAINTENANCE_STATUS,
} from "../../../../types/maintenance.type";
import { RegisterRow } from "../../../renters/components/forms/fields/RegisterRow";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import * as Yup from "yup";
import { useCompanyStore } from "../../../../common/states/store/Company";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { readFileAsBase64 } from "../../../../pages/home/VehicleSituation";
import { maintenanceUpdateOnlyOdometer } from "../../../../common/services/vehicles.api";
import InputFormik from "../../../../common/components/form/InputFormik";
import { IEmployee } from "@/types/employee.type";
import { getMechanics } from "@/common/services/employee.api";

type Props = {
  maintenance: { _id: string; licensePlate: string };
  onClose: () => void;
  data: IMaintenance;
};

export default function ModalStartMaintenance(props: Props) {
  const { notifySuccess, notifyError, notifyInfo } = useToastContext();
  const [submitResult, setSubmitResult] = useState<string | null>(null);
  const authCtx = useContext(AuthContext);
  const [odometroImageBase64, setOdometroImageBase64] = useState<string | null>(
    null
  );
  const userEmail = authCtx.auth?.user?.email;
  const { filterMode, selectedCompanies } = useCompanyStore();
  const [showOdometer, setShowOdometer] = useState(false);
  const [mechanics, setMechanics] = useState<IEmployee[]>([]);
  const singleSelectedCompany = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0];
    }
    return null;
  }, [filterMode, selectedCompanies]);
  useEffect(() => {
    if (singleSelectedCompany) {
      getMechanics().then((result) => {
        if (result.ok) {
          setMechanics(result.data);
        }
      });
    } else {
      setMechanics([]);
    }
  }, [singleSelectedCompany]);

  const validationSchema = Yup.object().shape({
    registers: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required("O campo tipo é obrigatório"),
        content: Yup.string().test(
          "content-required",
          "O campo conteúdo é obrigatório para o hodômetro",
          function (value) {
            const { type } = this.parent;
            if (type === "Odômetro") {
              return !!value;
            }
            return true;
          }
        ),
      })
    ),
  });

  const handleImageUpload = async (type: string, file: File) => {
    try {
      if (type === "Odômetro") {
        const base64Data = await readFileAsBase64(file);
        setOdometroImageBase64(base64Data);
      }
    } catch (error) {
      console.error("❌ Erro ao processar imagem:", error);
      notifyError(
        "Falha ao processar imagem de hodômetro. Por favor, tente novamente."
      );
    }
  };

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: {
      maintenanceId: props.maintenance._id,
      odometer: "",
      licensePlate: props.maintenance.licensePlate,
      mechanic: "",
      registers: [
        { type: "Frente do veículo", content: "" },
        { type: "Lateral direita do veículo", content: "" },
        { type: "Lateral esquerda do veículo", content: "" },
        { type: "Traseira do veículo", content: "" },
        { type: "Odômetro", content: "" },
        { type: "Foto de aérea da moto", content: "" },
      ],
      vehicle: {
        _id: props.data.vehicleId,
      },
      author: "",
      observations: [
        {
          text: "",
          date: "",
          author: "",
        },
      ],
    },
    onSubmit: async (data) => {
      const odometroEntry = formik.values.registers.find(
        (register) => register.type === "Odômetro"
      );

      let waitTypeOdometer = false;

      if (!data.registers || data.registers.length === 0) {
        notifyError(`Erro ao carregar imagens da manutenção: registros vazios`);
        return;
      }

      if (!odometroImageBase64 || !odometroEntry || !odometroEntry.content) {
        notifyError("A foto do hodômetro é obrigatória");
        return;
      } else {
        setShowOdometer(true);
        waitTypeOdometer = true;
        let odometer;
        // let odometerResponse;
        // if (!showOdometer) {
        //   odometerResponse = await odometerToTextPost(odometroImageBase64);
        //   odometer = odometerResponse["odometer"]
        // }

        // if (odometerResponse) {
        //   if ((!odometerResponse.ok && !showOdometer) || (!odometerResponse["success"] && !showOdometer)) {
        //     odometer = data.odometer
        //     notifyError('Falha ao processar a leitura do odômetro. Por favor, digite manualmente.');
        //     return;
        //   }
        //   notifySuccess(`odômetro extraído com sucesso: ${odometerResponse["odometer"]} km`);
        // }

        const updateMan = await maintenanceUpdateOnlyOdometer(
          odometer,
          props.maintenance._id
        );
        if (!updateMan.ok) {
          notifyError(
            updateMan.message || "Falha ao atualizar o hodômetro da manutenção"
          );
          return;
        }
      }

      if (waitTypeOdometer && !data.odometer) {
        notifyError("o hodômetro é obrigatório para iniciar a manutenção");
        return;
      }

      data.observations.forEach((observations) => {
        if (!observations.date) {
          observations.date = new Date().toISOString();
        }

        if (!observations.author) {
          observations.author = userEmail || "";
        }
      });

      const activationData = {
        maintenanceId: data.maintenanceId,
        status: MAINTENANCE_STATUS.Pending,
        mechanic: data.mechanic,
        author: (data.author = userEmail || ""),
        observations: data.observations,
        vehicleId: props.data.vehicleId,
      };

      notifySuccess("Revisão iniciada com sucesso!");
      setSubmitResult("success");

      const savePhotoDelivery = {
        maintenanceId: props.data._id,
        renterId: props.data.renterId,
        vehicleId: props.data.vehicleId,
        photosMaintenance: data.registers,
        date: new Date(),
        nameRenter: props.data?.name,
        licensePlate: props.data.licensePlate,
      };
      const resultPhoto = await maintenanceStartVisualRecords(
        savePhotoDelivery
      );

      const result = (await changeStatusMaintenance(
        activationData
      )) as unknown as { amountFine: number; ok: boolean; message: unknown };

      if (result.amountFine > 0) {
        notifyInfo(`${result.amountFine}`);
      }

      if (!result.ok) {
        notifyError(
          `Erro ao iniciar a manutenção: ${result.message as string}`
        );
        setSubmitResult("error");
        return;
      }

      if (!resultPhoto.ok) {
        notifyError(resultPhoto.message as string);
        setSubmitResult("error");
        return;
      }
      notifySuccess("Fotos salvas com sucesso");
    },
  });

  const observations = getIn(formik.values, "observations");
  const currentObservation = useMemo(() => {
    const total = observations.filter(({ date }) => !!date);
    return total.length;
  }, [observations]);

  const badgeColor = useMemo(
    () => (submitResult == "success" ? "text-greenPrimary" : "text-redPrimary"),
    [submitResult]
  );

  const registers = formik.values.registers;

  return (
    <Modal
      className="relative dark:text-black "
      show={true}
      onClose={() => props.onClose()}
    >
      <form onSubmit={formik.handleSubmit}>
        <FormikProvider value={formik}>
          <Modal.Header className="dark:text-black">
            <h2 className="dark:text-black">
              Iniciar manutenção da moto2: {props.maintenance.licensePlate}
            </h2>
          </Modal.Header>
          <Modal.Body
            className="w-full flex flex-wrap items-center justify-center border-gray"
            style={{
              minHeight: "calc(100vh - 300px)",
              height: "calc(100vh - 300px)",
            }}
          >
            <section className="grid grid-cols-1 my-4">
              <div className="grid gap-2 py-4">
                <h2 className="text-2xl font-semibold mb-4">
                  Fotos do Veículo
                </h2>
                {registers?.map((doc, index) => (
                  <div
                    key={index}
                    className={`space-x-3 px-3 py-2 my-0 rounded ${
                      index % 2 ? "bg-gray/5" : "bg-gray/25"
                    }`}
                  >
                    <RegisterRow
                      initialData={doc || {}}
                      name={`registers.${index}`}
                      onRemoveClick={() =>
                        formik.setFieldValue(
                          "registers",
                          registers.filter((_, i) => i !== index)
                        )
                      }
                      prefix="start-maintenance"
                      onImageUpload={handleImageUpload}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className=" disabled:text-black/50"
                  onClick={() => {
                    formik.setFieldValue(`registers.${registers.length}`, {
                      type: "",
                      content: "",
                      verified: false,
                    });
                  }}
                >
                  + campos para fotos
                </button>
              </div>
              <section>
                <ComboBoxFormik
                  label="Selecione o mecânico responsável pela manutenção:"
                  name={"mechanic"}
                  options={[
                    { value: "", label: "-" },
                    ...mechanics.map((mechanic) => ({
                      value: mechanic.name as string,
                      label: mechanic.name,
                    })),
                  ]}
                  className="w-full"
                />
                {showOdometer && (
                  <InputFormik
                    name="odometer"
                    label="Digite o hodômetro:"
                  ></InputFormik>
                )}
                <div className="w-full dark:text-black bg-gray/40 rounded my-4 p-3">
                  <h2 className="font-semibold text-2xl mb-4">Observações</h2>
                  <div>
                    <TextareaFormik
                      label={`Observação`}
                      name={`observations.${currentObservation}.text`}
                      className="min-h-64 w-full rounded shadow-md focus:border-white"
                    />
                  </div>
                </div>
              </section>
            </section>
            <div className="flex items-center justify-center text-center">
              <div className="pb-[24px]">
                <h2 className="font-semibold ">
                  Clique aqui para iniciar a manutenção dessa moto:
                </h2>
              </div>
              <div className="grid-div2">
                <ButtonWithLoader
                  isLoading={formik.isSubmitting}
                  disabled={showOdometer && !formik.values.odometer}
                >
                  Iniciar
                </ButtonWithLoader>
                <FaCheckCircle size={24} className={`${badgeColor}`} />
              </div>
            </div>
          </Modal.Body>
        </FormikProvider>
      </form>
    </Modal>
  );
}
